import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  CardFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
  getAllKycSubmissions,
  approveKyc,
  rejectKyc,
  IMAGE_BASE_URL,
  getAllDisputes,
  updateDisputeStatus,
  sendDisputePolicyEmail,
  getKycHistory,
} from "api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
  filterBar: {
    background: "linear-gradient(135deg, #f8f9fe 0%, #eef1f8 100%)",
    borderRadius: "12px",
    padding: "20px 24px",
    margin: "0 0 4px 0",
    border: "1px solid rgba(0,0,0,0.04)",
    position: "relative",
    zIndex: 1,
  },
  filterPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff",
    borderRadius: "10px",
    padding: "10px 18px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    fontWeight: 600,
    color: "#525f7f",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  filterPillActive: {
    background: "linear-gradient(135deg, #11cdef 0%, #1171ef 100%)",
    color: "#fff",
    border: "1px solid transparent",
    boxShadow: "0 4px 12px rgba(17,205,239,0.3)",
  },
  tableRow: {
    transition: "all 0.15s ease",
    position: "relative",
  },
  infoBlock: {
    background: "#fff",
    borderRadius: "8px",
    padding: "16px",
    border: "1px solid #e9ecef",
    marginBottom: "12px",
  },
  evidenceBox: {
    background: "#f6f9fc",
    border: "1px dashed #ced4da",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "12px",
  },
};

/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */

/** Group KYC submissions by user ID */
const groupKycByUser = (list) => {
  const grouped = new Map();

  list.forEach((item) => {
    const userId =
      item.user?.id || item.userId?._id || item.userId || item.user?.email;

    if (!grouped.has(userId)) {
      grouped.set(userId, []);
    }

    grouped.get(userId).push(item);
  });

  return Array.from(grouped.entries());
};

/** Normalise a raw KYC submission from the API into the shape the UI expects. */
const normalizeKyc = (raw) => ({
  id: raw._id,
  user: {
    id: raw.userId?._id,
    name: raw.userId?.name || "Unknown",
    email: raw.userId?.email || "",
    image: raw.userId?.image ? `${IMAGE_BASE_URL}/${raw.userId.image}` : null,
    country: raw.userId?.country || "—",
  },
  fullName: raw.fullName,
  documentType: raw.documentType,
  idType: raw.documentType
    ? raw.documentType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "—",
  documentNumber: raw.documentNumber,
  frontImage: raw.frontImage
    ? `${IMAGE_BASE_URL}/kyc/${raw.userId?._id || raw.userId?.id || raw.userId}/${raw.frontImage}`
    : null,
  backImage: raw.backImage
    ? `${IMAGE_BASE_URL}/kyc/${raw.userId?._id || raw.userId?.id || raw.userId}/${raw.backImage}`
    : null,
  status: raw.status
    ? raw.status.charAt(0).toUpperCase() + raw.status.slice(1)
    : "Pending",
  submittedOn: raw.createdAt
    ? new Date(raw.createdAt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—",
  documents: [
    ...(raw.frontImage
      ? [
          `${IMAGE_BASE_URL}/kyc/${raw.userId?._id || raw.userId?.id || raw.userId}/${raw.frontImage}`,
        ]
      : []),
    ...(raw.backImage
      ? [
          `${IMAGE_BASE_URL}/kyc/${raw.userId?._id || raw.userId?.id || raw.userId}/${raw.backImage}`,
        ]
      : []),
  ],
  createdAt: raw.createdAt,
  rejectionReason: raw.rejectionReason || raw.reason,
});

const getFormattedSource = (source) => {
  if (!source) return "Other";
  return source
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getStatusBadge = (status) => {
  const s = (status || "").toLowerCase();
  switch (s) {
    case "approved":
    case "resolved":
    case "refund_successfully":
      return (
        <Badge
          color="success"
          style={{ padding: "6px 10px", borderRadius: "6px" }}
        >
          {status === "refund_successfully" ? "Refunded" : status}
        </Badge>
      );
    case "pending":
    case "open":
      return (
        <Badge
          color="warning"
          style={{ padding: "6px 10px", borderRadius: "6px" }}
        >
          {status}
        </Badge>
      );
    case "rejected":
      return (
        <Badge
          color="danger"
          style={{ padding: "6px 10px", borderRadius: "6px" }}
        >
          {status}
        </Badge>
      );
    case "under_review":
      return (
        <Badge
          color="info"
          style={{ padding: "6px 10px", borderRadius: "6px" }}
        >
          Under Review
        </Badge>
      );
    default:
      return (
        <Badge
          color="secondary"
          style={{ padding: "6px 10px", borderRadius: "6px" }}
        >
          {status}
        </Badge>
      );
  }
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
const Kyc = () => {
  // Refs for preventing race conditions
  const activeRequestsRef = useRef(new AbortController());

  const renderPaginationItems = (
    currentPage,
    totalItems,
    limit,
    onPageChange,
  ) => {
    const totalPagesCount = Math.ceil(totalItems / limit);
    if (totalPagesCount <= 1) return null;

    const pages = [];
    const delta = 2;

    pages.push(
      <PaginationItem key={1} active={currentPage === 1}>
        <PaginationLink
          href="#pablo"
          onClick={(e) => {
            e.preventDefault();
            onPageChange(1);
          }}
        >
          1
        </PaginationLink>
      </PaginationItem>,
    );

    if (currentPage > delta + 2) {
      pages.push(
        <PaginationItem key="start-ellipsis" disabled>
          <PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>
            ...
          </PaginationLink>
        </PaginationItem>,
      );
    }

    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(
        <PaginationItem key={i} active={currentPage === i}>
          <PaginationLink
            href="#pablo"
            onClick={(e) => {
              e.preventDefault();
              onPageChange(i);
            }}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (currentPage < totalPagesCount - delta - 1) {
      pages.push(
        <PaginationItem key="end-ellipsis" disabled>
          <PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>
            ...
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (totalPagesCount > 1) {
      pages.push(
        <PaginationItem
          key={totalPagesCount}
          active={currentPage === totalPagesCount}
        >
          <PaginationLink
            href="#pablo"
            onClick={(e) => {
              e.preventDefault();
              onPageChange(totalPagesCount);
            }}
          >
            {totalPagesCount}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return pages;
  };

  const [activeTab, setActiveTab] = useState("KYC Queue");

  // KYC State
  const [kycList, setKycList] = useState([]);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState(null);
  const [kycStatusFilter, setKycStatusFilter] = useState("All");
  const [kycSearch, setKycSearch] = useState("");
  const [kycPage, setKycPage] = useState(1);
  const [kycTotalPages, setKycTotalPages] = useState(1);
  const [kycTotal, setKycTotal] = useState(0);
  const KYC_LIMIT = 10;

  // Disputes State
  const [disputeList, setDisputeList] = useState([]);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputePage, setDisputePage] = useState(1);
  const [disputeTotal, setDisputeTotal] = useState(0);
  const [disputeTotalPages, setDisputeTotalPages] = useState(1);
  const DISPUTE_LIMIT = 10;

  // Modal States
  const [disputeDrawerOpen, setDisputeDrawerOpen] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);

  /** Fetch KYC submissions from the real API */
  const fetchKycSubmissions = useCallback(
    async (page = 1, search = "", status = "All") => {
      // Cancel previous request if exists
      if (activeRequestsRef.current) {
        activeRequestsRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      activeRequestsRef.current = abortController;

      setKycLoading(true);
      setKycError(null);

      try {
        const statusParam = status === "All" ? "" : status.toLowerCase();

        const res = await getAllKycSubmissions(
          page,
          KYC_LIMIT,
          search,
          statusParam,
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        if (res?.status && res?.payload) {
          // Normalize KYC submissions
          const list = (res.payload.kycSubmissions || []).map(normalizeKyc);

          // Group by user
          const grouped = groupKycByUser(list);

          // Sort each group by date (newest first)
          const sorted = grouped.map(([userId, records]) => [
            userId,
            records.sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            ),
          ]);

          // Flatten for table (latest-first global order)
          const flattened = sorted.flatMap(([userId, records]) => records);

          setKycList(flattened);
          setKycTotal(res.payload.total || 0);
          setKycTotalPages(res.payload.totalPages || 1);
          setKycPage(res.payload.page || 1);
        } else {
          setKycList([]);
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
          return;
        }
        console.error("KYC fetch error:", err);
        setKycError("Failed to load KYC submissions.");
      } finally {
        if (!abortController.signal.aborted) {
          setKycLoading(false);
        }
      }
    },
    [],
  );

  /** Fetch disputes from real API */
  const fetchDisputes = useCallback(async (page = 1) => {
    setDisputeLoading(true);
    try {
      const res = await getAllDisputes(page, DISPUTE_LIMIT);
      if (res?.status && res?.payload) {
        setDisputeList(res.payload.disputes || []);
        setDisputeTotal(res.payload.total || 0);
        setDisputeTotalPages(res.payload.totalPages || 1);
        setDisputePage(res.payload.page || 1);
      } else {
        setDisputeList([]);
      }
    } catch (err) {
      console.error("Dispute fetch error:", err);
      setDisputeList([]);
    } finally {
      setDisputeLoading(false);
    }
  }, []);

  // Debounced search for KYC
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchKycSubmissions(1, kycSearch, kycStatusFilter);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [kycSearch, kycStatusFilter, fetchKycSubmissions]);

  // Initial load
  useEffect(() => {
    fetchKycSubmissions(1, kycSearch, kycStatusFilter);
    fetchDisputes(1);

    // Cleanup function to abort any ongoing requests
    return () => {
      if (activeRequestsRef.current) {
        activeRequestsRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleKycAction = (action, item, event) => {
    if (event) event.preventDefault();

    if (action === "Review Docs") {
      Swal.fire({
        title: `KYC Documents: ${item.user.name}`,
        html: `
                <div class="text-left mb-3">
                    <p class="mb-1"><strong>ID Type:</strong> ${item.idType}</p>
                    <p class="mb-3 text-muted" style="font-size: 13px;">Review the submitted images carefully for authenticity.</p>
                </div>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
                    ${item.documents
                      .map(
                        (doc, idx) => `
                        <div style="background: #f8f9fe; padding: 10px; border-radius: 12px; border: 1px solid #e9ecef; text-align: center;">
                            <p class="text-xs font-weight-bold mb-2 text-uppercase text-muted">${idx === 0 ? "Front Side" : "Back Side"}</p>
                            <img src="${doc}" style="width: 100%; height: 160px; object-fit: cover; border-radius: 8px; cursor: pointer; border: 1px solid #dee2e6;" 
                                 onclick="window.open('${doc}', '_blank')" 
                                 onerror="this.onerror=null; this.src='https://placehold.co/200x160/f8f9fe/525f7f?text=Image+Not+Found'"
                            />
                            <div class="mt-2 text-xs">
                                <a href="${doc}" target="_blank" class="text-primary"><i class="fas fa-external-link-alt"></i> Open Full Image</a>
                            </div>
                        </div>
                    `,
                      )
                      .join("")}
                </div>
            `,
        width: "650px",
        confirmButtonText: "Close Viewer",
        confirmButtonColor: "#5e72e4",
      });
      return;
    }

    // HISTORY BUTTON - API se saari history dikhaye (current + previous)
    if (action === "View History") {
      // Get KYC ID from the item
      const kycId = item.id;

      if (!kycId) {
        Swal.fire({
          title: "Error",
          text: "KYC ID not found",
          icon: "error",
          confirmButtonColor: "#f5365c",
        });
        return;
      }

      // Make API call for history (no loader)
      getKycHistory(kycId)
        .then((response) => {
          if (response?.success && response) {
            // Get current KYC and history from response
            const currentKyc = response.currentKyc;
            const historyList = response.history || [];

            // Combine current and history
            let allSubmissions = [];

            // Add current KYC
            if (currentKyc) {
              allSubmissions.push({
                id: currentKyc._id,
                fullName: currentKyc.fullName,
                documentType: currentKyc.documentType,
                documentNumber: currentKyc.documentNumber,
                status: currentKyc.status,
                createdAt: currentKyc.createdAt,
                frontImage: currentKyc.frontImage,
                backImage: currentKyc.backImage,
                isCurrent: true,
              });
            }

            // Add history submissions
            historyList.forEach((history) => {
              allSubmissions.push({
                id: history._id,
                fullName: history.fullName,
                documentType: history.documentType,
                documentNumber: history.documentNumber,
                status: history.status,
                createdAt: history.createdAt,
                frontImage: history.frontImage,
                backImage: history.backImage,
                rejectionReason: history.rejectionReason,
                isCurrent: false,
              });
            });

            // Sort by date (newest first)
            const sortedSubmissions = allSubmissions.sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            );

            if (sortedSubmissions.length === 0) {
              Swal.fire({
                title: "No History Found",
                text: `No KYC submissions found`,
                icon: "info",
                confirmButtonColor: "#5e72e4",
              });
              return;
            }

            // Helper function to get status badge HTML
            const getStatusHtml = (status) => {
              const statusLower = (status || "").toLowerCase();
              switch (statusLower) {
                case "approved":
                  return '<span class="badge badge-success">✓ Approved</span>';
                case "rejected":
                  return '<span class="badge badge-danger">✗ Rejected</span>';
                case "pending":
                  return '<span class="badge badge-warning"> Pending</span>';
                case "under_review":
                  return '<span class="badge badge-info"> Under Review</span>';
                default:
                  return `<span class="badge badge-secondary">${status || "N/A"}</span>`;
              }
            };

            // Helper function to format document type
            const formatDocType = (type) => {
              if (!type) return "N/A";
              return type
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase());
            };

            // Helper function to get image URL
            const getImageUrl = (imageName, userId) => {
              if (!imageName) return null;
              return `${IMAGE_BASE_URL}/kyc/${userId}/${imageName}`;
            };

            // Generate timeline HTML
            const timelineHtml = sortedSubmissions
              .map((submission, index) => {
                const isLatest = index === 0;
                const formattedDate = new Date(
                  submission.createdAt,
                ).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });

                // Get document images
                const documents = [];
                if (submission.frontImage) {
                  documents.push({
                    url: getImageUrl(submission.frontImage, item.user.id),
                    label: "Front Side",
                  });
                }
                if (submission.backImage) {
                  documents.push({
                    url: getImageUrl(submission.backImage, item.user.id),
                    label: "Back Side",
                  });
                }

                return `
                            <div style="display: flex; margin-bottom: 20px; position: relative;">
                                <div style="width: 40px; text-align: center; flex-shrink: 0;">
                                    <div style="
                                        width: 32px; 
                                        height: 32px; 
                                        background: ${isLatest ? "#5e72e4" : "#e9ecef"};
                                        border-radius: 50%;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        margin: 0 auto;
                                        color: white;
                                    ">
                                        <i class="fas ${isLatest ? "fa-star" : "fa-file-alt"}"></i>
                                    </div>
                                    ${
                                      index < sortedSubmissions.length - 1
                                        ? `
                                        <div style="
                                            position: absolute;
                                            top: 32px;
                                            left: 20px;
                                            width: 2px;
                                            height: calc(100% - 32px);
                                            background: #dee2e6;
                                        "></div>
                                    `
                                        : ""
                                    }
                                </div>
                                <div style="flex: 1; padding-left: 15px;">
                                    <div style="
                                        background: white;
                                        border-radius: 12px;
                                        padding: 15px;
                                        border: 1px solid ${isLatest ? "#5e72e4" : "#e9ecef"};
                                        box-shadow: ${isLatest ? "0 2px 8px rgba(94,114,228,0.1)" : "none"};
                                    ">
                                        <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                                            <div>
                                                <strong style="color: #172b4d; font-size: 14px;">
                                                    Submission #${sortedSubmissions.length - index}
                                                    ${isLatest ? ' <span style="color: #5e72e4; font-size: 11px;">(Latest)</span>' : ""}
                                                </strong>
                                                <div style="font-size: 11px; color: #6c757d; margin-top: 4px;">
                                                    ${formattedDate}
                                                </div>
                                            </div>
                                            <div>${getStatusHtml(submission.status)}</div>
                                        </div>
                                        
                                        <div style="display: grid; grid-template-columns: auto 1fr; gap: 8px; font-size: 13px; margin-top: 12px;">
                                            <div><strong>Document Type:</strong></div>
                                            <div>${formatDocType(submission.documentType)}</div>
                                            
                                            <div><strong>Document Number:</strong></div>
                                            <div>${submission.documentNumber || "N/A"}</div>
                                            
                                            <div><strong>Full Name:</strong></div>
                                            <div>${submission.fullName}</div>
                                            
                                            ${
                                              submission.rejectionReason
                                                ? `
                                                <div><strong style="color: #f5365c;">Rejection Reason:</strong></div>
                                                <div style="color: #721c24; background: #fff5f5; padding: 6px 10px; border-radius: 6px; margin-top: 4px;">
                                                    ${submission.rejectionReason}
                                                </div>
                                            `
                                                : ""
                                            }
                                        </div>
                                        
                                        ${
                                          documents.length > 0
                                            ? `
                                            <div style="margin-top: 15px;">
                                                <strong style="font-size: 12px; color: #525f7f;">Documents:</strong>
                                                <div style="display: flex; gap: 10px; margin-top: 8px; flex-wrap: wrap;">
                                                    ${documents
                                                      .map(
                                                        (doc, idx) => `
                                                        <div style="cursor: pointer;" onclick="window.open('${doc.url}', '_blank')">
                                                            <img 
                                                                src="${doc.url}" 
                                                                style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px; border: 1px solid #dee2e6;"
                                                                onerror="this.onerror=null; this.src='https://placehold.co/80x80/f8f9fe/525f7f?text=No+Image'"
                                                            />
                                                            <div style="font-size: 10px; text-align: center; margin-top: 4px;">${doc.label}</div>
                                                        </div>
                                                    `,
                                                      )
                                                      .join("")}
                                                </div>
                                            </div>
                                        `
                                            : '<div class="text-muted mt-2">No documents uploaded</div>'
                                        }
                                    </div>
                                </div>
                            </div>
                        `;
              })
              .join("");

            // Show complete history modal
            Swal.fire({
              title: `
                                <i class="fas fa-history" style="color: #5e72e4;"></i>
                                <span style="font-size: 20px; font-weight: 600; color: #172b4d;">KYC History</span>
                            
                        `,
              html: `
                            <div style="font-size: 13px; color: #6c757d; margin-top: 8px;">
                                ${item.user.name} • ${item.user.email}
                            </div>
                            <div style="max-height: 550px; overflow-y: auto; padding: 10px;">
                                <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px; border-radius: 12px; margin-bottom: 20px;">
                                    <div style="font-size: 12px; opacity: 0.9;">Current KYC Status</div>
                                    <div style="font-size: 20px; font-weight: 700;">${currentKyc?.status || item.status}</div>
                                </div>
                                ${timelineHtml}
                            </div>
                            <div class="text-muted text-center mt-3 pt-2 border-top" style="font-size: 11px;">
                                <i class="fas fa-info-circle mr-1"></i> 
                                Showing ${sortedSubmissions.length} total submission${sortedSubmissions.length !== 1 ? "s" : ""}
                                | Click images to view full size
                            </div>
                        `,
              width: "800px",
              confirmButtonText: "Close",
              confirmButtonColor: "#5e72e4",
              showCloseButton: true,
              customClass: {
                popup: "kyc-history-modal",
                title:
                  "d-flex align-items-center justify-content-center gap-2 h-auto",
                htmlContainer: "kyc-history-content",
              },
              didOpen: () => {
                const style = document.createElement("style");
                style.textContent = `
                                .kyc-history-modal {
                                    border-radius: 20px !important;
                                }
                                .kyc-history-modal .swal2-html-container {
                                    padding: 1.5rem !important;
                                }
                                .badge {
                                    display: inline-block;
                                    padding: 6px 12px;
                                    font-size: 11px;
                                    font-weight: 600;
                                    border-radius: 6px;
                                }
                                .badge-success { background-color: #2dce89; color: #fff; }
                                .badge-danger { background-color: #f5365c; color: #fff; }
                                .badge-warning { background-color: #fb6340; color: #fff; }
                                .badge-info { background-color: #11cdef; color: #fff; }
                                .badge-secondary { background-color: #6c757d; color: #fff; }
                                ::-webkit-scrollbar {
                                    width: 6px;
                                }
                                ::-webkit-scrollbar-track {
                                    background: #f1f1f1;
                                    border-radius: 10px;
                                }
                                ::-webkit-scrollbar-thumb {
                                    background: #c1c1c1;
                                    border-radius: 10px;
                                }
                            `;
                document.head.appendChild(style);
              },
            });
          } else {
            Swal.fire({
              title: "No History Found",
              text: response?.message || "No KYC history available.",
              icon: "info",
              confirmButtonColor: "#5e72e4",
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching KYC history:", error);
          Swal.fire({
            title: "Error",
            text:
              error.message || "Failed to load KYC history. Please try again.",
            icon: "error",
            confirmButtonColor: "#f5365c",
          });
        });
      return;
    }

    // REJECT BUTTON
    if (action === "Reject") {
      Swal.fire({
        title: "Reject KYC",
        html: `<p class="text-sm text-muted mb-2">Provide a reason for rejecting <strong>${item.user.name}</strong>'s KYC application.</p>`,
        input: "textarea",
        inputPlaceholder: "e.g. Document unclear, information mismatch…",
        inputAttributes: {
          rows: 3,
          minlength: 10,
          maxlength: 500,
        },
        showCancelButton: true,
        confirmButtonColor: "#f5365c",
        cancelButtonColor: "#8898aa",
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        preConfirm: (reason) => {
          const trimmed = reason ? reason.trim() : "";
          if (!trimmed) {
            Swal.showValidationMessage("Rejection reason is required.");
            return false;
          }
          if (trimmed.length < 10) {
            Swal.showValidationMessage(
              "Rejection reason must be at least 10 characters long.",
            );
            return false;
          }
          if (trimmed.length > 500) {
            Swal.showValidationMessage(
              "Rejection reason cannot exceed 500 characters.",
            );
            return false;
          }
          // Simple check to ensure it contains at least some alphanumeric characters (meaningful text)
          if (!/[a-zA-Z0-9]/.test(trimmed)) {
            Swal.showValidationMessage(
              "Please provide a meaningful reason containing letters or numbers.",
            );
            return false;
          }
          return trimmed;
        },
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        const reason = r.value;

        const originalList = [...kycList];
        setKycList((prev) =>
          prev.map((k) =>
            k.id === item.id
              ? { ...k, status: "Rejected", rejectionReason: reason }
              : k,
          ),
        );

        try {
          const res = await rejectKyc(item.id, { reason });
          if (res?.status) {
            Swal.fire({
              title: "Rejected!",
              text: "KYC application has been rejected.",
              icon: "success",
              timer: 3000,
              showConfirmButton: false,
            });
          } else {
            throw new Error(res?.message || "Rejection failed");
          }
        } catch (err) {
          setKycList(originalList);
          Swal.fire("Error", err.message || "Failed to reject KYC.", "error");
        } finally {
          fetchKycSubmissions(kycPage, kycSearch, kycStatusFilter);
        }
      });
      return;
    }

    // APPROVE BUTTON
    if (action === "Approve") {
      Swal.fire({
        title: "Approve KYC",
        text: `Approve KYC application for ${item.user.name}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#2dce89",
        cancelButtonColor: "#8898aa",
        confirmButtonText: "Yes, approve",
        cancelButtonText: "Cancel",
      }).then(async (r) => {
        if (!r.isConfirmed) return;

        const originalList = [...kycList];
        setKycList((prev) =>
          prev.map((k) =>
            k.id === item.id ? { ...k, status: "Approved" } : k,
          ),
        );

        try {
          const res = await approveKyc(item.id);
          if (res?.status) {
            Swal.fire({
              title: "Approved!",
              text: "KYC application has been approved.",
              icon: "success",
              timer: 3000,
              showConfirmButton: false,
            });
          } else {
            throw new Error(res?.message || "Approval failed");
          }
        } catch (err) {
          setKycList(originalList);
          Swal.fire("Error", err.message || "Failed to approve KYC.", "error");
        } finally {
          fetchKycSubmissions(kycPage, kycSearch, kycStatusFilter);
        }
      });
      return;
    }
  };
  const handleDisputeAction = async (action, dispute, event) => {
    if (event) event.preventDefault();

    if (action === "Review Dispute") {
      setSelectedDispute(dispute);
      setDisputeDrawerOpen(true);
      return;
    }

    if (action === "Add Notes") {
      Swal.fire({
        title: "Add Internal Note",
        input: "textarea",
        inputValue: dispute.adminNotes || "",
        inputPlaceholder: "Type your internal investigation notes here...",
        showCancelButton: true,
        confirmButtonColor: "#5e72e4",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const newNotes = result.value || "";
            await updateDisputeStatus(dispute._id, {
              status: dispute.status,
              adminNotes: newNotes,
            });
            setSelectedDispute((prev) => ({
              ...prev,
              adminNotes: newNotes,
            }));
            Swal.fire({
              title: "Saved!",
              text: "Note attached to dispute.",
              icon: "success",
              timer: 3000,
              showConfirmButton: false,
            });
            fetchDisputes(disputePage);
          } catch (err) {
            Swal.fire(
              "Error",
              err?.response?.data?.message || "Failed to add notes.",
              "error",
            );
          }
        }
      });
      return;
    }

    if (action === "Send Policy Email") {
      Swal.fire({
        title: "Send Policy Alert Email?",
        text: "This will send an email notifying the user that this dispute's amount cannot be reversed due to policy (released escrow).",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#fb6340",
        cancelButtonColor: "#8898aa",
        confirmButtonText: "Yes, Send Email",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            Swal.fire({
              title: "Sending Email...",
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              },
            });
            await sendDisputePolicyEmail(dispute._id);
            Swal.fire({
              title: "Sent!",
              text: "Policy alert email successfully sent to the user.",
              icon: "success",
              timer: 3000,
              showConfirmButton: false,
            });
            setDisputeDrawerOpen(false);
          } catch (err) {
            Swal.fire(
              "Error",
              err?.response?.data?.message || "Failed to send email.",
              "error",
            );
          }
        }
      });
      return;
    }

    // Map frontend actions to backend status
    const statusMap = {
      Resolve: "under_review",
      Refund: "refund_successfully",
      Reject: "rejected",
    };

    const backendStatus = statusMap[action];
    if (!backendStatus) return;

    const confirmText =
      action === "Refund"
        ? "This will check if the escrow is in held status and refund the amount to the user's wallet."
        : `Are you sure you want to ${action === "Resolve" ? "mark this dispute as under review" : "reject this claim"}?`;

    const swalOptions = {
      title: `${action === "Resolve" ? "Under Review" : action} Dispute`,
      html: `<p class="text-sm text-muted mb-2">${confirmText}</p>`,
      showCancelButton: true,
      confirmButtonColor:
        action === "Reject"
          ? "#f5365c"
          : action === "Refund"
            ? "#fb6340"
            : "#2dce89",
      cancelButtonColor: "#8898aa",
      confirmButtonText:
        action === "Refund"
          ? "Issue Refund"
          : action === "Resolve"
            ? "Under Review"
            : `Yes, ${action}`,
      cancelButtonText: "Cancel",
    };

    if (action === "Reject") {
      swalOptions.input = "textarea";
      swalOptions.inputPlaceholder = "Provide rejection notes (optional)...";
      swalOptions.inputAttributes = { rows: 2 };
    }

    const swalResult = await Swal.fire(swalOptions);
    if (!swalResult.isConfirmed) return;

    const adminNotes = swalResult.value || "";

    try {
      const res = await updateDisputeStatus(dispute._id, {
        status: backendStatus,
        adminNotes: adminNotes || "",
      });

      if (res?.status) {
        setDisputeDrawerOpen(false);
        fetchDisputes(disputePage);

        const successMsg =
          action === "Refund"
            ? res.message || "Refund issued successfully!"
            : `Dispute ${action.toLowerCase()}ed successfully.`;

        Swal.fire({
          title: "Action Applied!",
          text: successMsg,
          icon: "success",
          timer: 4000,
          showConfirmButton: false,
        });
      } else {
        throw new Error(res?.message || "Action failed");
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to process action.";
      Swal.fire("Error", errMsg, "error");
    }
  };

  // Custom CSS for Dropdowns
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
            .dropdown-menu { z-index: 9999 !important; position: absolute !important; will-change: transform; }
            .btn-group .dropdown-menu { z-index: 9999 !important; }
            .table-responsive { overflow-x: visible !important; overflow-y: visible !important; }
            .table { overflow: visible !important; }
            td, th { overflow: visible !important; }
        `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <>
      <CommonHeader />
      <Container
        className="mt--7"
        fluid
        style={{
          position: "relative",
          zIndex: 1,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {/* Tabs / Filter Section */}
        <div
          class="d-flex flex-column flex-md-row w-100"
          style={{
            ...styles.filterBar,
            display: "flex",
            gap: "10px",
            overflowX: "auto",
          }}
        >
          {["KYC Queue", "Disputes Workspace"].map((tab) => (
            <div
              key={tab}
              className="text-center"
              style={{
                ...styles.filterPill,
                ...(activeTab === tab ? styles.filterPillActive : {}),
              }}
              onClick={() => setActiveTab(tab)}
            >
              <i
                className={`fas ${tab === "KYC Queue" ? "fa-id-card" : "fa-gavel"}`}
              />
              {tab}
            </div>
          ))}
        </div>

        {/* Main Card */}
        <Row className="mt-4">
          <Col>
            <Card
              className="shadow"
              style={{
                borderRadius: "16px",
                border: "none",
                overflow: "hidden",
              }}
            >
              <CardHeader
                className="border-0 bg-white"
                style={{ borderRadius: "16px 16px 0 0", padding: "24px" }}
              >
                <Col
                  md="12"
                  className="p-0 d-flex justify-content-between align-items-center flex-wrap"
                >
                  <div>
                    <h3
                      className="mb-0"
                      style={{
                        fontSize: "20px",
                        fontWeight: 700,
                        color: "#172b4d",
                      }}
                    >
                      <i className="ni ni-badge text-orange mr-2" /> {activeTab}
                    </h3>
                  </div>
                  {activeTab === "KYC Queue" && (
                    <div
                      className="d-flex align-items-center flex-wrap "
                      style={{ gap: "15px" }}
                    >
                      <InputGroup
                        style={{
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #e2e8f0",
                          background: "#fff",
                          width: "250px",
                        }}
                      >
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText
                            style={{ background: "#fff", border: "none" }}
                          >
                            <i
                              className="fas fa-search"
                              style={{ color: "#8898aa", fontSize: "13px" }}
                            />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Search Users..."
                          value={kycSearch}
                          onChange={(e) => setKycSearch(e.target.value)}
                          style={{
                            border: "none",
                            boxShadow: "none",
                            fontSize: "13px",
                          }}
                        />
                      </InputGroup>
                      <Input
                        type="select"
                        className="status-select"
                        value={kycStatusFilter}
                        onChange={(e) => setKycStatusFilter(e.target.value)}
                        style={{
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0",
                          fontSize: "13px",
                          color: "#525f7f",
                          width: "150px",
                        }}
                      >
                        <option value="All">All Statuses</option>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </Input>
                    </div>
                  )}
                </Col>
              </CardHeader>

              <CardBody className="p-0" style={{}}>
                <TabContent activeTab={activeTab}>
                  {/* KYC TAB */}
                  <TabPane tabId="KYC Queue">
                    <div
                      className="table-responsive"
                      style={{ width: "100%", overflowX: "auto" }}
                    >
                      <Table className="align-items-center table-flush" hover>
                        <thead className="thead-light">
                          <tr>
                            <th scope="col" style={{ padding: "16px 24px" }}>
                              User
                            </th>
                            <th scope="col">Full Name</th>
                            <th scope="col">Country</th>
                            <th scope="col">ID Type</th>
                            <th scope="col">Status</th>
                            <th scope="col">Submitted On</th>
                            <th
                              scope="col"
                              className="text-right"
                              style={{ paddingRight: "24px" }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {kycLoading ? (
                            <tr>
                              <td colSpan="7" className="text-center py-5">
                                <Spinner color="primary" />
                                <div
                                  className="text-muted mt-2"
                                  style={{ fontSize: "13px" }}
                                >
                                  Loading submissions…
                                </div>
                              </td>
                            </tr>
                          ) : kycError ? (
                            <tr>
                              <td
                                colSpan="7"
                                className="text-center py-5 text-danger"
                              >
                                <i className="fas fa-exclamation-circle fa-2x mb-2" />
                                <div>{kycError}</div>
                                <Button
                                  size="sm"
                                  color="primary"
                                  className="mt-2"
                                  onClick={() =>
                                    fetchKycSubmissions(
                                      kycPage,
                                      kycSearch,
                                      kycStatusFilter,
                                    )
                                  }
                                >
                                  Retry
                                </Button>
                              </td>
                            </tr>
                          ) : kycList.length === 0 ? (
                            <tr>
                              <td
                                colSpan="7"
                                className="text-center py-5 text-muted"
                              >
                                <i className="fas fa-id-card fa-2x mb-2" />
                                <div>No KYC submissions found.</div>
                              </td>
                            </tr>
                          ) : (
                            kycList.map((item) => (
                              <tr key={item.id} style={styles.tableRow}>
                                <td style={{ padding: "16px 24px" }}>
                                  <div className="d-flex align-items-center">
                                    {(() => {
                                      const rawImage = item?.user?.image;
                                      let imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                        item?.user?.name || "User",
                                      )}&background=random&color=fff`;

                                      if (rawImage) {
                                        const fileName = rawImage
                                          .split("/")
                                          .pop();
                                        imageUrl = `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${fileName}`;
                                      }

                                      return (
                                        <img
                                          src={imageUrl}
                                          alt={item?.user?.name || "User"}
                                          className="rounded-circle mr-3"
                                          style={{
                                            width: "40px",
                                            height: "40px",
                                            minWidth: "40px",
                                            objectFit: "cover",
                                            border: "1px solid #e9ecef",
                                          }}
                                          onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                              item?.user?.name || "User",
                                            )}&background=random&color=fff`;
                                          }}
                                        />
                                      );
                                    })()}

                                    <div className="d-flex flex-column">
                                      <span className="font-weight-bold text-dark">
                                        {item?.user?.name || "-"}
                                      </span>
                                      <span className="text-xs text-muted">
                                        {item?.user?.email || "-"}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  style={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    minWidth: "150px",
                                  }}
                                >
                                  <span className="font-weight-bold">
                                    {item.fullName || item.user.name}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-muted">
                                    {item.user.country}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    minWidth: "120px",
                                  }}
                                >
                                  <i
                                    className={`mr-2 fas ${item.documentType === "passport" ? "fa-passport" : "fa-id-card"} text-muted`}
                                  />
                                  {item.idType}
                                </td>
                                <td>{getStatusBadge(item.status)}</td>
                                <td className="text-muted text-sm">
                                  {item.submittedOn}
                                </td>
                                <td
                                  className="text-right"
                                  style={{
                                    paddingRight: "24px",
                                    position: "relative",
                                  }}
                                >
                                  <div className="d-flex justify-content-end align-items-center">
                                    <Button
                                      color="info"
                                      outline
                                      size="sm"
                                      className="px-3 mr-2"
                                      style={{
                                        borderRadius: "8px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        height: "32px",
                                      }}
                                      onClick={(e) =>
                                        handleKycAction("Review Docs", item, e)
                                      }
                                    >
                                      <i className="fas fa-eye mr-2" /> View
                                    </Button>
                                    <Button
                                      color="secondary"
                                      outline
                                      size="sm"
                                      className="px-3 mr-2"
                                      style={{
                                        borderRadius: "8px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        height: "32px",
                                      }}
                                      onClick={(e) =>
                                        handleKycAction("View History", item, e)
                                      }
                                    >
                                      <i className="fas fa-history mr-2" />{" "}
                                      History
                                    </Button>

                                    {item?.status !== "Approved" &&
                                      item?.status !== "Rejected" && (
                                        <UncontrolledDropdown>
                                          <DropdownToggle
                                            className="btn-icon-only shadow-sm"
                                            role="button"
                                            size="sm"
                                            style={{
                                              background: "#fff",
                                              border: "none",
                                              borderRadius: "8px",
                                              color: "#6c757d",
                                              width: "32px",
                                              height: "32px",
                                              padding: 0,
                                              boxShadow:
                                                "0 1px 3px rgba(0,0,0,0.08)",
                                            }}
                                            onClick={(e) => e.preventDefault()}
                                          >
                                            <i className="fas fa-ellipsis-v" />
                                          </DropdownToggle>
                                          <DropdownMenu
                                            className="dropdown-menu-arrow shadow border-0"
                                            right
                                          >
                                            <DropdownItem
                                              onClick={(e) =>
                                                handleKycAction(
                                                  "Approve",
                                                  item,
                                                  e,
                                                )
                                              }
                                            >
                                              <i className="fas fa-check-circle text-success mr-2" />{" "}
                                              Approve
                                            </DropdownItem>
                                            <DropdownItem
                                              onClick={(e) =>
                                                handleKycAction(
                                                  "Reject",
                                                  item,
                                                  e,
                                                )
                                              }
                                            >
                                              <i className="fas fa-times-circle text-danger mr-2" />{" "}
                                              Reject
                                            </DropdownItem>
                                          </DropdownMenu>
                                        </UncontrolledDropdown>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                    {/* Pagination */}
                    {!kycLoading && kycTotalPages > 1 && (
                      <CardFooter className="py-4 border-top bg-transparent d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          Showing {(kycPage - 1) * KYC_LIMIT + 1} to{" "}
                          {Math.min(kycPage * KYC_LIMIT, kycTotal)} of{" "}
                          {kycTotal} submissions
                        </span>
                        <nav aria-label="...">
                          <Pagination
                            className="pagination justify-content-end mb-0"
                            listClassName="justify-content-end mb-0"
                          >
                            <PaginationItem disabled={kycPage <= 1}>
                              <PaginationLink
                                href="#pablo"
                                onClick={(e) => {
                                  e.preventDefault();
                                  fetchKycSubmissions(
                                    kycPage - 1,
                                    kycSearch,
                                    kycStatusFilter,
                                  );
                                }}
                                tabIndex="-1"
                              >
                                <i className="fas fa-angle-left" />
                              </PaginationLink>
                            </PaginationItem>

                            {renderPaginationItems(
                              kycPage,
                              kycTotal,
                              KYC_LIMIT,
                              (page) =>
                                fetchKycSubmissions(
                                  page,
                                  kycSearch,
                                  kycStatusFilter,
                                ),
                            )}

                            <PaginationItem disabled={kycPage >= kycTotalPages}>
                              <PaginationLink
                                href="#pablo"
                                onClick={(e) => {
                                  e.preventDefault();
                                  fetchKycSubmissions(
                                    kycPage + 1,
                                    kycSearch,
                                    kycStatusFilter,
                                  );
                                }}
                              >
                                <i className="fas fa-angle-right" />
                              </PaginationLink>
                            </PaginationItem>
                          </Pagination>
                        </nav>
                      </CardFooter>
                    )}
                  </TabPane>

                  {/* DISPUTES TAB */}
                  <TabPane tabId="Disputes Workspace">
                    <div className="table-responsive">
                      <Table className="align-items-center table-flush" hover>
                        <thead className="thead-light">
                          <tr>
                            <th scope="col" style={{ padding: "16px 24px" }}>
                              Dispute ID
                            </th>
                            <th scope="col">User</th>
                            <th scope="col">Module Type</th>
                            <th scope="col">Reason</th>
                            <th scope="col">Related Transaction</th>
                            <th scope="col">Payment Amount</th>
                            <th scope="col">Status</th>
                            <th
                              scope="col"
                              className="text-right"
                              style={{ paddingRight: "24px" }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {disputeLoading ? (
                            <tr>
                              <td colSpan="8" className="text-center py-5">
                                <Spinner color="primary" />
                                <div
                                  className="text-muted mt-2"
                                  style={{ fontSize: "13px" }}
                                >
                                  Loading disputes…
                                </div>
                              </td>
                            </tr>
                          ) : disputeList.length === 0 ? (
                            <tr>
                              <td
                                colSpan="8"
                                className="text-center py-5 text-muted"
                              >
                                <i className="fas fa-gavel fa-2x mb-2" />
                                <div>No disputes found.</div>
                              </td>
                            </tr>
                          ) : (
                            disputeList.map((dsp) => (
                              <tr key={dsp._id} style={styles.tableRow}>
                                <td style={{ padding: "16px 24px" }}>
                                  <span className="font-weight-bold text-dark">
                                    DSP-
                                    {dsp._id
                                      .substring(dsp._id.length - 6)
                                      .toUpperCase()}
                                  </span>
                                  <div className="text-xs text-muted mt-1">
                                    {new Date(dsp.createdAt).toLocaleDateString(
                                      "en-GB",
                                      {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      },
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {(() => {
                                      const rawImage = dsp.userId?.image;
                                      let imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                        dsp.userId?.name || "User",
                                      )}&background=random&color=fff`;

                                      if (rawImage) {
                                        const fileName = rawImage
                                          .split("/")
                                          .pop();
                                        imageUrl = `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${fileName}`;
                                      }

                                      return (
                                        <img
                                          src={imageUrl}
                                          alt={dsp.userId?.name || "User"}
                                          className="rounded-circle mr-2"
                                          style={{
                                            width: "28px",
                                            height: "28px",
                                            objectFit: "cover",
                                            border: "1px solid #e9ecef",
                                          }}
                                          onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                              dsp.userId?.name || "User",
                                            )}&background=random&color=fff`;
                                          }}
                                        />
                                      );
                                    })()}
                                    <div>
                                      <span
                                        className="font-weight-bold d-block text-dark"
                                        style={{ lineHeight: "1.2" }}
                                      >
                                        {dsp.userId?.name || "Unknown"}
                                      </span>
                                      <span
                                        className="text-xs text-muted d-block mt-1"
                                        style={{ fontSize: "11px" }}
                                      >
                                        {dsp.userId?.email || ""}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className="font-weight-bold text-dark text-capitalize"
                                    style={{ fontSize: "13px" }}
                                  >
                                    {getFormattedSource(
                                      dsp.transactionId?.source,
                                    )}
                                  </div>
                                  <div className="mt-1">
                                    <Badge
                                      color={
                                        dsp.transactionId?.type === "credit"
                                          ? "success"
                                          : dsp.transactionId?.type === "debit"
                                            ? "danger"
                                            : "secondary"
                                      }
                                      className="text-uppercase"
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: "600",
                                        padding: "4px 8px",
                                      }}
                                    >
                                      {dsp.transactionId?.type || "N/A"}
                                    </Badge>
                                  </div>
                                </td>
                                <td
                                  style={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    minWidth: "150px",
                                  }}
                                >
                                  <span className="text-sm">
                                    {dsp.reasonLabel || dsp.reason}
                                  </span>
                                </td>
                                <td>
                                  <Badge
                                    color="light"
                                    style={{ color: "#525f7f" }}
                                  >
                                    <i className="fas fa-exchange-alt mr-1" />{" "}
                                    {dsp.transactionId?.transactionId || "N/A"}
                                  </Badge>
                                </td>
                                <td>
                                  <span
                                    className="font-weight-bold text-dark"
                                    style={{ fontSize: "14px" }}
                                  >
                                    {dsp.transactionId?.currency?.toUpperCase() ||
                                      "USD"}{" "}
                                    {dsp.transactionId?.amount !== undefined
                                      ? dsp.transactionId.amount
                                      : "0"}
                                  </span>
                                </td>
                                <td>{getStatusBadge(dsp.status)}</td>
                                <td
                                  className="text-right"
                                  style={{ paddingRight: "24px" }}
                                >
                                  <Button
                                    color="primary"
                                    size="sm"
                                    style={{
                                      borderRadius: "8px",
                                      boxShadow:
                                        "0 4px 6px rgba(50,50,93,0.11)",
                                    }}
                                    onClick={(e) =>
                                      handleDisputeAction(
                                        "Review Dispute",
                                        dsp,
                                        e,
                                      )
                                    }
                                  >
                                    <i className="fas fa-book-open mr-2" />{" "}
                                    Review
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Dispute Workspace Drawer / Modal */}
      {selectedDispute && (
        <Modal
          isOpen={disputeDrawerOpen}
          toggle={() => setDisputeDrawerOpen(false)}
          size="lg"
          centered
        >
          <ModalHeader
            toggle={() => setDisputeDrawerOpen(false)}
            className="bg-secondary border-0 pb-0"
          >
            <div className="d-flex align-items-center">
              <span className="font-weight-bold" style={{ fontSize: "18px" }}>
                Dispute Workspace
              </span>
              <Badge
                color="light"
                className="ml-3"
                style={{ fontSize: "13px", padding: "6px 10px" }}
              >
                DSP-
                {selectedDispute._id
                  .substring(selectedDispute._id.length - 6)
                  .toUpperCase()}
              </Badge>
            </div>
          </ModalHeader>
          <ModalBody className="p-0 bg-secondary">
            <Row className="m-0">
              {/* Left Side Context */}
              <Col
                md="8"
                className="p-4 border-right bg-white"
                style={{ borderBottomLeftRadius: "16px" }}
              >
                <h4 className="mb-4 text-dark font-weight-900 border-bottom pb-3">
                  <i className="fas fa-exclamation-circle text-warning mr-2" />
                  {selectedDispute.reasonLabel || selectedDispute.reason}
                </h4>

                <Row className="mb-3">
                  <Col md="6">
                    <div style={styles.infoBlock}>
                      <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">
                        User Initiating
                      </div>
                      <div className="d-flex align-items-center mt-2">
                        {(() => {
                          const rawImage = selectedDispute.userId?.image;
                          let imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            selectedDispute.userId?.name || "User",
                          )}&background=random&color=fff`;

                          if (rawImage) {
                            const fileName = rawImage.split("/").pop();
                            imageUrl = `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${fileName}`;
                          }

                          return (
                            <img
                              src={imageUrl}
                              alt={selectedDispute.userId?.name || "User"}
                              className="rounded-circle mr-2"
                              style={{
                                width: "26px",
                                height: "26px",
                                objectFit: "cover",
                                border: "1px solid #e9ecef",
                              }}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  selectedDispute.userId?.name || "User",
                                )}&background=random&color=fff`;
                              }}
                            />
                          );
                        })()}
                        <span className="font-weight-bold text-dark">
                          {selectedDispute.userId?.name || "Unknown"}
                        </span>
                      </div>
                      <div className="text-xs text-muted mt-1">
                        {selectedDispute.userId?.email}
                      </div>
                    </div>
                  </Col>
                  <Col md="6">
                    <div style={styles.infoBlock}>
                      <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">
                        Disputed Transaction
                      </div>
                      <div
                        className="font-weight-bold text-dark mt-2"
                        style={{ wordBreak: "break-all" }}
                      >
                        <i className="fas fa-receipt text-primary mr-2" />
                        {selectedDispute.transactionId?.transactionId || "N/A"}
                      </div>
                      <div className="text-xs text-muted mt-1">
                        ${selectedDispute.transactionId?.amount || 0} •{" "}
                        {selectedDispute.transactionId?.createdAt
                          ? new Date(
                              selectedDispute.transactionId.createdAt,
                            ).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="mb-4">
                  <h6 className="text-uppercase text-muted ls-1 mb-2">
                    Payment Type
                  </h6>
                  <div
                    style={{ ...styles.infoBlock, background: "#f8f9fe" }}
                    className="d-flex align-items-center"
                  >
                    <span className="text-sm font-weight-bold text-dark mr-2">
                      Type:{" "}
                    </span>
                    <Badge
                      color={
                        selectedDispute.transactionId?.type === "credit"
                          ? "success"
                          : selectedDispute.transactionId?.type === "debit"
                            ? "danger"
                            : "secondary"
                      }
                      className="text-uppercase"
                      style={{ fontSize: "12px", padding: "6px 10px" }}
                    >
                      {selectedDispute.transactionId?.type || "N/A"}
                    </Badge>
                  </div>
                </div>

                {/* Escrow Information Block */}
                <h6 className="text-uppercase text-muted ls-1 mb-2">
                  Escrow Status
                </h6>
                <div
                  style={{ ...styles.infoBlock, background: "#f8f9fe" }}
                  className="mb-4"
                >
                  {selectedDispute.escrowInfo?.isEscrowRelated ? (
                    selectedDispute.escrowInfo?.found ? (
                      <div>
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <span className="text-sm font-weight-bold text-dark">
                              Escrow ID:{" "}
                            </span>
                            <span
                              className="text-sm text-muted"
                              style={{ wordBreak: "break-all" }}
                            >
                              {selectedDispute.escrowInfo.escrowUuid}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm font-weight-bold text-dark">
                              Status:{" "}
                            </span>
                            <Badge
                              color={
                                selectedDispute.escrowInfo.escrowStatus ===
                                "held"
                                  ? "warning"
                                  : "secondary"
                              }
                            >
                              {(
                                selectedDispute.escrowInfo.escrowStatus || "N/A"
                              ).toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-muted">
                        <i className="fas fa-exclamation-triangle mr-2 text-warning" />
                        Escrow related but not found in the database.
                      </div>
                    )
                  ) : (
                    <div className="text-sm text-muted">
                      <i className="fas fa-info-circle mr-2 text-info" />
                      Escrow is not applicable for this transaction type.
                    </div>
                  )}
                </div>

                <h6 className="text-uppercase text-muted ls-1 mb-3">
                  Dispute Description & Evidence
                </h6>
                <div style={styles.evidenceBox}>
                  <div className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                    <i className="mr-1 fas fa-align-left"></i> Description
                  </div>
                  <div className="text-sm font-italic text-dark px-2 border-left border-primary mt-2">
                    "{selectedDispute.description}"
                  </div>
                </div>

                {selectedDispute.adminNotes && (
                  <div className="mt-3">
                    <h6 className="text-uppercase text-muted ls-1 mb-2">
                      Admin Notes
                    </h6>
                    <div
                      style={{
                        ...styles.infoBlock,
                        background: "#fffdf5",
                        border: "1px solid #ffeeba",
                      }}
                    >
                      <span className="text-sm text-dark">
                        {selectedDispute.adminNotes}
                      </span>
                    </div>
                  </div>
                )}
              </Col>

              {/* Right Side Admin Actions */}
              <Col md="4" className="p-4 pt-5">
                {["pending", "under_review"].includes(
                  selectedDispute.status,
                ) && (
                  <>
                    <h6 className="text-uppercase text-muted ls-1 mb-3">
                      Dispute Actions
                    </h6>

                    {selectedDispute.status !== "under_review" && (
                      <Button
                        block
                        color="success"
                        className="mb-3 text-left"
                        onClick={() =>
                          handleDisputeAction("Resolve", selectedDispute)
                        }
                      >
                        <i className="fas fa-check-double mr-2" /> Mark Under
                        Review
                      </Button>
                    )}

                    {!(
                      selectedDispute.escrowInfo?.isEscrowRelated &&
                      selectedDispute.escrowInfo?.found &&
                      selectedDispute.escrowInfo?.escrowStatus !== "held"
                    ) && (
                      <Button
                        block
                        color="warning"
                        className="mb-3 text-left"
                        outline
                        onClick={() =>
                          handleDisputeAction("Refund", selectedDispute)
                        }
                      >
                        <i className="fas fa-hand-holding-usd mr-2" /> Issue
                        Refund
                      </Button>
                    )}

                    <Button
                      block
                      color="danger"
                      className="mb-3 text-left"
                      outline
                      onClick={() =>
                        handleDisputeAction("Reject", selectedDispute)
                      }
                    >
                      <i className="fas fa-times mr-2" /> Reject Claim
                    </Button>

                    <hr className="my-4 border-light" />
                  </>
                )}

                <Button
                  block
                  color="primary"
                  className="mb-0 text-left"
                  outline
                  onClick={() =>
                    handleDisputeAction("Add Notes", selectedDispute)
                  }
                >
                  <i className="fas fa-sticky-note mr-2" /> Add Internal Notes
                </Button>

                {selectedDispute.adminNotes && (
                  <div
                    className="mt-3 p-3 bg-light rounded border text-left"
                    style={{ borderLeft: "4px solid #5e72e4" }}
                  >
                    <div className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                      Internal Investigation Notes:
                    </div>
                    <div
                      className="text-sm text-dark font-italic"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      "{selectedDispute.adminNotes}"
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default Kyc;
